import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  Alert,
  Button,
  Card,
  Col,
  Result,
  Row,
  Spin,
  Tag,
  Typography,
} from "antd";
import {
  CheckCircleOutlined,
  CreditCardOutlined,
  LoginOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  YoutubeOutlined,
} from "@ant-design/icons";
import { useAuth } from "../context/AuthContext";
import { useSelection } from "../SelectionContext";
import { ELSA_BRAND_ASSETS } from "../config/brandAssets";
import "./PaymentPage.css";

const { Paragraph, Text, Title } = Typography;
const PENDING_REFERENCE_KEY = "elsa_subscription_pending_reference";

const formatAmount = (amount, currency = "XOF") =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(Number(amount) || 0);

const PaymentPage = () => {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { selectedCompany } = useSelection();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const requestedPlanId = Number.parseInt(query.get("plan"), 10) || null;
  const [billingPeriod, setBillingPeriod] = useState(
    query.get("billing") === "annual" ? "annual" : "monthly",
  );
  const [selectedPlanId, setSelectedPlanId] = useState(requestedPlanId);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [payment, setPayment] = useState(null);
  const [companyQuotes, setCompanyQuotes] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [error, setError] = useState(null);
  const reference =
    query.get("reference") || localStorage.getItem(PENDING_REFERENCE_KEY) || null;
  const authenticatedCompanyHeaders = useMemo(() => {
    const companyId = Number(selectedCompany);
    return {
      Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      "Content-Type": "application/json",
      ...(Number.isInteger(companyId) && companyId > 0
        ? { "X-Company-Id": String(companyId) }
        : {}),
    };
  }, [selectedCompany]);

  const loadConfiguration = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch("/api/subscription-online/public/config");
      const payload = await response.json();
      if (!response.ok || !payload.success) {
        throw new Error(payload.message || "Impossible de charger les offres");
      }
      setConfig(payload.data);
      setSelectedPlanId((current) =>
        payload.data.plans.some((plan) => plan.id === current)
          ? current
          : payload.data.plans[0]?.id || null,
      );
    } catch (loadError) {
      setError(loadError.message || "Les offres sont indisponibles.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadConfiguration();
  }, [loadConfiguration]);

  const loadPaymentStatus = useCallback(async () => {
    if (!reference || !isAuthenticated) return null;
    setStatusLoading(true);
    try {
      const response = await fetch(
        `/api/subscription-online/payments/${encodeURIComponent(reference)}`,
        {
          headers: authenticatedCompanyHeaders,
        },
      );
      const payload = await response.json();
      if (!response.ok || !payload.success) {
        throw new Error(payload.message || "Statut du paiement indisponible");
      }
      setPayment(payload.data.payment);
      if (
        payload.data.payment.payment_status === "failed" ||
        payload.data.payment.activation_status === "applied"
      ) {
        localStorage.removeItem(PENDING_REFERENCE_KEY);
      }
      return payload.data.payment;
    } catch (statusError) {
      setError(statusError.message || "Impossible de vérifier le paiement.");
      return null;
    } finally {
      setStatusLoading(false);
    }
  }, [authenticatedCompanyHeaders, isAuthenticated, reference]);

  useEffect(() => {
    if (!reference || !isAuthenticated) return undefined;
    void loadPaymentStatus();
    const interval = window.setInterval(async () => {
      const current = await loadPaymentStatus();
      if (
        current?.payment_status === "failed" ||
        current?.activation_status === "applied"
      ) {
        window.clearInterval(interval);
      }
    }, 4000);
    return () => window.clearInterval(interval);
  }, [isAuthenticated, loadPaymentStatus, reference]);

  const selectedPlan = useMemo(
    () => config?.plans.find((plan) => plan.id === selectedPlanId) || null,
    [config, selectedPlanId],
  );
  useEffect(() => {
    if (!isAuthenticated || !selectedPlanId) {
      setCompanyQuotes(null);
      return undefined;
    }
    const controller = new AbortController();
    const loadQuotes = async () => {
      setQuoteLoading(true);
      setCompanyQuotes(null);
      try {
        const periods = ["monthly", "annual"];
        const responses = await Promise.all(
          periods.map(async (period) => {
            const response = await fetch(
              `/api/subscription-online/quote?plan_id=${selectedPlanId}&billing_period=${period}`,
              {
                headers: authenticatedCompanyHeaders,
                signal: controller.signal,
              },
            );
            const payload = await response.json();
            if (!response.ok || !payload.success) {
              throw new Error(payload.message || "Devis indisponible");
            }
            return [period, payload.data.quote];
          }),
        );
        setCompanyQuotes(Object.fromEntries(responses));
      } catch (quoteError) {
        if (quoteError.name !== "AbortError") {
          setCompanyQuotes(null);
          setError(quoteError.message || "Devis indisponible");
        }
      } finally {
        if (!controller.signal.aborted) setQuoteLoading(false);
      }
    };
    void loadQuotes();
    return () => controller.abort();
  }, [authenticatedCompanyHeaders, isAuthenticated, selectedPlanId]);

  const displayedQuotes = isAuthenticated ? companyQuotes : selectedPlan;
  const quote = displayedQuotes?.[billingPeriod] || null;

  const startPayment = async () => {
    if (!selectedPlan || !quote) return;
    setStarting(true);
    setError(null);
    try {
      const response = await fetch("/api/subscription-online/checkout", {
        method: "POST",
        headers: authenticatedCompanyHeaders,
        body: JSON.stringify({
          plan_id: selectedPlan.id,
          billing_period: billingPeriod,
        }),
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) {
        throw new Error(payload.message || "YengaPay n'a pas pu être ouvert");
      }
      localStorage.setItem(PENDING_REFERENCE_KEY, payload.data.reference);
      window.location.assign(payload.data.checkout_url);
    } catch (checkoutError) {
      setError(checkoutError.message || "Paiement impossible.");
      setStarting(false);
    }
  };

  const signupTarget = selectedPlan
    ? `/signup?plan=${selectedPlan.id}&billing=${billingPeriod}`
    : "/signup";
  const paymentTarget = selectedPlan
    ? `/payment?plan=${selectedPlan.id}&billing=${billingPeriod}`
    : "/payment";
  const loginTarget = `/login?next=${encodeURIComponent(paymentTarget)}`;

  if (payment?.payment_status === "paid" && payment.activation_status === "applied") {
    return (
      <div className="subscription-checkout subscription-checkout--result">
        <Result
          status="success"
          title="Paiement confirmé et abonnement activé"
          subTitle={`${payment.plan_name} est actif pour ${payment.duration_months} mois. Référence : ${payment.reference}`}
          extra={[
            <Link to="/souscription" key="subscription"><Button type="primary">Voir ma souscription</Button></Link>,
            <Link to="/accueil" key="home"><Button>Accéder à ELSA GESTION</Button></Link>,
          ]}
        />
      </div>
    );
  }

  return (
    <div className="subscription-checkout">
      <Helmet>
        <title>Souscription sécurisée | ELSA GESTION</title>
        <meta name="description" content="Choisissez votre plan ELSA GESTION et réglez votre abonnement avec YengaPay." />
      </Helmet>

      <header className="subscription-checkout__header">
        <Link to="/" aria-label="Retour à la landing page">
          <img
            src={ELSA_BRAND_ASSETS.logoHorizontalColor}
            alt="ELSA GESTION — Gardez le contrôle, où que vous soyez."
          />
        </Link>
        <Tag color="blue" icon={<SafetyCertificateOutlined />}>Paiement sécurisé YengaPay</Tag>
      </header>

      <main className="subscription-checkout__main">
        <div className="subscription-checkout__intro">
          <Text className="subscription-checkout__eyebrow">ABONNEMENT ELSA GESTION</Text>
          <Title>Choisissez votre formule</Title>
          <Paragraph>
            Payez mensuellement ou bénéficiez d'un mois offert avec la souscription annuelle. Le montant est recalculé et contrôlé par ELSA avant l'ouverture de YengaPay.
          </Paragraph>
        </div>

        {error && <Alert type="error" showIcon message={error} closable onClose={() => setError(null)} />}

        {reference && isAuthenticated && payment?.payment_status !== "failed" && (
          <Alert
            type={payment?.payment_status === "paid" ? "success" : "info"}
            showIcon
            icon={payment?.payment_status === "paid" ? <CheckCircleOutlined /> : <Spin size="small" />}
            message={
              payment?.payment_status === "paid"
                ? "Paiement confirmé — activation en cours"
                : "Vérification du paiement YengaPay en cours"
            }
            description={`Référence : ${reference}`}
            action={<Button size="small" icon={<ReloadOutlined />} loading={statusLoading} onClick={() => void loadPaymentStatus()}>Vérifier</Button>}
          />
        )}

        {payment?.payment_status === "failed" && (
          <Alert
            type="error"
            showIcon
            message="Le paiement n'a pas abouti"
            description="Vous pouvez sélectionner à nouveau votre offre et relancer un paiement. Votre compte d'essai reste disponible."
          />
        )}

        <Spin spinning={loading} tip="Chargement des offres…">
          <Row gutter={[18, 18]} className="subscription-checkout__plans">
            {(config?.plans || []).map((plan) => (
              <Col xs={24} md={12} key={plan.id}>
                <Card
                  hoverable
                  className={`subscription-checkout__plan ${selectedPlanId === plan.id ? "is-selected" : ""}`}
                  onClick={() => setSelectedPlanId(plan.id)}
                >
                  <div className="subscription-checkout__plan-title">
                    <div>
                      <Title level={3}>{plan.name}</Title>
                      <Text type="secondary">{plan.description || "Offre ELSA GESTION"}</Text>
                    </div>
                    {selectedPlanId === plan.id && <CheckCircleOutlined />}
                  </div>
                  <strong>{formatAmount(plan.price, plan.currency)} <small>/ Warehouse / mois</small></strong>
                  <ul>
                    {(plan.features || []).slice(0, 5).map((feature) => <li key={feature}>✓ {feature}</li>)}
                  </ul>
                </Card>
              </Col>
            ))}
          </Row>

          {selectedPlan && quote && (
            <Card className="subscription-checkout__summary" bordered={false}>
              <div className="subscription-checkout__periods" role="group" aria-label="Période de souscription">
                <button type="button" className={billingPeriod === "monthly" ? "is-active" : ""} onClick={() => setBillingPeriod("monthly")}>
                  Mensuel <span>{formatAmount(displayedQuotes.monthly.total_amount, quote.currency)}</span>
                </button>
                <button type="button" className={billingPeriod === "annual" ? "is-active" : ""} onClick={() => setBillingPeriod("annual")}>
                  Annuel <span>{formatAmount(displayedQuotes.annual.total_amount, quote.currency)} · 1 mois offert</span>
                </button>
              </div>

              <div className="subscription-checkout__total">
                <div>
                  <Text type="secondary">Total à régler maintenant</Text>
                  <Title level={2}>{formatAmount(quote.total_amount, quote.currency)}</Title>
                  <Text>
                    {quote.billable_warehouse_count} Warehouse(s) × {formatAmount(
                      quote.warehouse_unit_price,
                      quote.currency,
                    )} / mois
                  </Text>
                  {billingPeriod === "annual" && (
                    <Text className="subscription-checkout__discount">
                      Remise : {formatAmount(quote.discount_amount, quote.currency)}
                    </Text>
                  )}
                </div>
                <CreditCardOutlined />
              </div>

              {!isAuthenticated ? (
                <div className="subscription-checkout__auth-actions">
                  <Alert type="info" showIcon message="Créez d'abord votre espace ELSA" description="Le paiement sera ainsi rattaché de façon sûre à votre entreprise." />
                  <Link to={signupTarget}><Button type="primary" size="large">Créer mon compte</Button></Link>
                  <Link to={loginTarget}><Button size="large" icon={<LoginOutlined />}>J'ai déjà un compte</Button></Link>
                </div>
              ) : (
                <Button
                  type="primary"
                  size="large"
                  block
                  icon={<CreditCardOutlined />}
                  loading={starting || quoteLoading}
                  disabled={!config?.payment_enabled}
                  onClick={startPayment}
                >
                  Payer {formatAmount(quote.total_amount, quote.currency)} avec YengaPay
                </Button>
              )}

              {!config?.payment_enabled && (
                <Alert
                  type="warning"
                  showIcon
                  message="Paiement en ligne temporairement désactivé"
                  description="Le SysAdmin doit valider la configuration YengaPay avant l'ouverture des souscriptions. Votre essai gratuit reste disponible."
                />
              )}
            </Card>
          )}
        </Spin>

        <a
          className="subscription-checkout__youtube"
          href={config?.youtube_url || "https://www.youtube.com/@elsatechnologies7922"}
          target="_blank"
          rel="noopener noreferrer"
        >
          <YoutubeOutlined /> Voir les tutoriels ELSA GESTION
        </a>
      </main>
    </div>
  );
};

export default PaymentPage;
